import { leaveQuotaService } from "@/api/leave-quotas";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { LeaveQuota } from "@/types";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { leaveTypeOptions } from "@/utils/leaveType";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

export default function LeaveQuotas() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showDialog, setShowDialog] = useState(false);
  const [editingYear, setEditingYear] = useState<number | null>(null);
  const [formData, setFormData] = useState<{
    year: number;
    quotas: Record<string, number | string>;
  }>({
    year: new Date().getFullYear(),
    quotas: {},
  });

  const { data: quotas = [], isLoading: isLoadingQuotas } = useQuery({
    queryKey: ["leave-quotas"],
    queryFn: leaveQuotaService.getAll,
  });

  const createMutation = useMutation({
    mutationFn: leaveQuotaService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leave-quotas"] });
      toast({
        title: "Quota created",
        description: "Leave quota for the year has been set",
      });
      setShowDialog(false);
      resetForm();
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, "Failed to create quota");
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ year, data }: { year: number; data: Partial<LeaveQuota> }) =>
      leaveQuotaService.update(year, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leave-quotas"] });
      toast({
        title: "Quota updated",
        description: "Leave quota details have been updated",
      });
      setShowDialog(false);
      resetForm();
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, "Failed to update quota");
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: leaveQuotaService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leave-quotas"] });
      toast({
        title: "Quota deleted",
        description: "Leave quota has been removed",
      });
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, "Failed to delete quota");
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Filter quotas: only include those with numeric value > 0
    const filteredQuotas: Record<string, number> = {};
    Object.entries(formData.quotas).forEach(([key, value]) => {
      const numValue = Number(value);
      if (value !== "" && !isNaN(numValue) && numValue > 0) {
        filteredQuotas[key] = numValue;
      }
    });

    const payload = {
      year: formData.year,
      quotas: filteredQuotas,
    };

    if (editingYear) {
      updateMutation.mutate({ year: editingYear, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const resetForm = () => {
    const initialQuotas: Record<string, number | string> = {};
    leaveTypeOptions.forEach((lt) => {
      initialQuotas[lt.value] = "";
    });
    setFormData({
      year: new Date().getFullYear(),
      quotas: initialQuotas,
    });
    setEditingYear(null);
  };

  const handleEdit = (quota: LeaveQuota) => {
    setEditingYear(quota.year);
    // Keys in quota.quotas should already be Enums
    const currentQuotas: Record<string, number | string> = { ...quota.quotas };

    // Ensure all enum types are present
    leaveTypeOptions.forEach((lt) => {
      if (
        currentQuotas[lt.value] === undefined ||
        currentQuotas[lt.value] === 0
      ) {
        currentQuotas[lt.value] = "";
      }
    });

    setFormData({
      year: quota.year,
      quotas: currentQuotas,
    });
    setShowDialog(true);
  };

  const handleDelete = (quota: LeaveQuota) => {
    if (confirm(`Are you sure you want to delete quotas for ${quota.year}?`)) {
      deleteMutation.mutate(quota.year);
    }
  };

  const handleQuotaChange = (leaveType: string, value: string) => {
    // Allow empty string or numbers
    if (value === "" || /^\d+$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        quotas: {
          ...prev.quotas,
          [leaveType]: value === "" ? "" : parseInt(value),
        },
      }));
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-foreground">
              Leave Quota Management
            </h2>
            <p className="text-muted-foreground mt-1">
              Manage yearly leave quotas for employees
            </p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowDialog(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Quota
          </Button>
        </div>

        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle>Yearly Quotas</CardTitle>
            <CardDescription>{quotas.length} records found</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Year</TableHead>
                    <TableHead>Quotas (Days)</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingQuotas ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8">
                        Loading quotas...
                      </TableCell>
                    </TableRow>
                  ) : (
                    quotas.map((quota) => (
                      <TableRow key={quota.year}>
                        <TableCell className="font-medium text-lg">
                          {quota.year}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(quota.quotas).map(
                              ([key, value]) => {
                                // Find name in options using the key (which is the Enum)
                                const lt = leaveTypeOptions.find(
                                  (l) => l.value === key,
                                );
                                const name = lt ? lt.label : key;
                                return (
                                  <span
                                    key={key}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground"
                                  >
                                    {name}: {value}
                                  </span>
                                );
                              },
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(quota)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDelete(quota)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingYear ? `Edit Quota for ${editingYear}` : "Set New Quota"}
            </DialogTitle>
            <DialogDescription>
              {editingYear
                ? "Update leave allocation for this year"
                : "Define leave allocation for a new year"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="year">Year</Label>
              <Input
                id="year"
                type="number"
                value={formData.year}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    year: parseInt(e.target.value) || new Date().getFullYear(),
                  })
                }
                disabled={!!editingYear} // Disable year editing if updating
                required
              />
            </div>

            <div className="space-y-4 border-t pt-4">
              <h4 className="font-medium text-sm text-muted-foreground">
                Leave Allocations (Days)
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {leaveTypeOptions.map((lt) => (
                  <div key={lt.value} className="space-y-2">
                    <Label
                      htmlFor={`lt-${lt.value}`}
                      className="text-xs uppercase font-semibold text-muted-foreground"
                    >
                      {lt.label}
                    </Label>
                    <Input
                      id={`lt-${lt.value}`}
                      type="text"
                      inputMode="numeric"
                      pattern="\d*"
                      placeholder="0"
                      value={
                        formData.quotas[lt.value] === undefined
                          ? ""
                          : formData.quotas[lt.value]
                      }
                      onChange={(e) =>
                        handleQuotaChange(lt.value, e.target.value)
                      }
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingYear ? "Update" : "Create"} Quota
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
